import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import {
  Close as CloseIcon,
  CallEnd as CallEndIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
} from '@mui/icons-material';
import type { AppVoiceState, AppVoiceUiStatus } from '../../services/appTwilioVoiceService';

function formatCallDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

const DTMF_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'] as const;

function statusLabel(status: AppVoiceUiStatus): string {
  switch (status) {
    case 'fetching_token':
      return 'Preparing call…';
    case 'connecting':
      return 'Connecting…';
    case 'ringing':
      return 'Ringing…';
    case 'connected':
      return 'Connected — use the keypad after the prompt to enter an extension (e.g. 115).';
    case 'disconnecting':
      return 'Ending call…';
    case 'error':
      return 'Call error';
    default:
      return '';
  }
}

interface AppVoiceCallPanelProps {
  open: boolean;
  voiceState: AppVoiceState;
  isMuted: boolean;
  onClose: () => void;
  onHangUp: () => void;
  onToggleMute: () => void;
  onDigit: (digit: string) => void;
}

export function AppVoiceCallPanel({
  open,
  voiceState,
  isMuted,
  onClose,
  onHangUp,
  onToggleMute,
  onDigit,
}: AppVoiceCallPanelProps) {
  const [callElapsedSec, setCallElapsedSec] = useState(0);
  const callTickStartRef = useRef<number | null>(null);

  const busy =
    voiceState.status === 'fetching_token' ||
    voiceState.status === 'connecting' ||
    voiceState.status === 'disconnecting';

  const inCall =
    voiceState.status === 'ringing' ||
    voiceState.status === 'connected' ||
    busy;

  const canClose = voiceState.status === 'idle' || voiceState.status === 'error';

  useEffect(() => {
    const countWhile =
      open &&
      (voiceState.status === 'ringing' ||
        voiceState.status === 'connected' ||
        voiceState.status === 'disconnecting');

    if (!countWhile) {
      if (!open) {
        callTickStartRef.current = null;
        setCallElapsedSec(0);
      }
      return;
    }

    if (callTickStartRef.current == null) {
      callTickStartRef.current = Date.now();
      setCallElapsedSec(0);
    }

    const tick = () => {
      const start = callTickStartRef.current;
      if (start == null) return;
      setCallElapsedSec(Math.floor((Date.now() - start) / 1000));
    };

    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [open, voiceState.status]);

  return (
    <Dialog
      open={open}
      onClose={canClose ? onClose : undefined}
      fullWidth
      maxWidth="xs"
      slotProps={{
        paper: {
          sx: { borderRadius: 2 },
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        Call support
        {canClose && (
          <IconButton aria-label="Close" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {voiceState.status === 'error' && voiceState.errorMessage
            ? voiceState.errorMessage
            : statusLabel(voiceState.status)}
        </Typography>

        {inCall && (
          <Typography
            variant="h5"
            component="p"
            sx={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600, letterSpacing: 1, mb: 2, textAlign: 'center' }}
            aria-live="polite"
          >
            {formatCallDuration(callElapsedSec)}
          </Typography>
        )}

        {inCall && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
              <IconButton
                color={isMuted ? 'warning' : 'default'}
                onClick={onToggleMute}
                aria-label={isMuted ? 'Unmute microphone' : 'Mute microphone'}
                sx={{ border: '1px solid', borderColor: 'divider' }}
              >
                {isMuted ? <MicOffIcon /> : <MicIcon />}
              </IconButton>
              <Button
                variant="contained"
                color="error"
                startIcon={<CallEndIcon />}
                onClick={onHangUp}
                disabled={busy}
              >
                Hang up
              </Button>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Keypad (DTMF)
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 1,
                mb: 1,
              }}
            >
              {DTMF_KEYS.map((key) => (
                <Button
                  key={key}
                  fullWidth
                  variant="outlined"
                  onClick={() => onDigit(key)}
                  disabled={busy}
                  sx={{ minHeight: 44, fontSize: '1.1rem' }}
                >
                  {key}
                </Button>
              ))}
            </Box>
          </>
        )}

        {voiceState.status === 'error' && (
          <Button variant="outlined" fullWidth onClick={onClose} sx={{ mt: 1 }}>
            Dismiss
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
